import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';
import { ProtectedRouteProps } from '../types';

interface ExtendedProtectedRouteProps extends ProtectedRouteProps {
  role?: 'admin' | 'manager';
}

const ProtectedRoute: React.FC<ExtendedProtectedRouteProps> = ({ children, userType, role }) => {
  const { isLoading, isAuthenticated, isInitialized, userType: currentUserType, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth is being initialized
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return (
      <Navigate 
        to="/login" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // 인증됐지만 userType 미확정(토큰 갱신/초기화 중)이면 redirect 대신 로딩 유지.
  // null → brand fallback(/sample-request=projects) 오포워딩 race 방지.
  if (userType && !currentUserType) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check user type if specified
  if (userType && currentUserType !== userType) {
    // Wrong user type - redirect to appropriate page
    const redirectTo = currentUserType === 'admin' ? '/admin/dashboard' : '/sample-request';
    return <Navigate to={redirectTo} replace />;
  }

  // Check specific role for admin users
  if (role && currentUserType === 'admin') {
    const adminUser = user as any; // Type assertion for admin-specific properties
    if (!adminUser?.role || adminUser.role !== role) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">
              You don't have permission to access this page.
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Required role: {role}, Your role: {adminUser?.role || 'none'}
            </p>
          </div>
        </div>
      );
    }
  }

  // All checks passed, render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;